// eslint-disable-next-line import/no-extraneous-dependencies
import { validate } from './JoyDocValidator.js';
import JoyDocSchemaJson from '../constants/JoyDocSchema.json';
import pkg from '../../package.json';

/**
 * Validates the schema of a JoyDoc document.
 *
 * @param {Object} doc - The JoyDoc document to validate.
 * @returns {Object|undefined} - Returns an error object if validation fails, otherwise undefined.
 */

const validateSchema = (doc) => {
  const { $joyfillSchemaVersion } = JoyDocSchemaJson;
  const { version: sdkVersion } = pkg;


  const passedSchemaVersion = doc?.v || '1.0.0';

  const schemaErrorObject = {
    code: 'ERROR_SCHEMA_VALIDATION',
    message: 'Error detected during schema validation',
    details: {
      schemaVersion: $joyfillSchemaVersion,
      sdkVersion,
    }
  };

  const schemaVersionErrorObject = {
    code: 'ERROR_SCHEMA_VERSION',
    message: 'Error detected with targeted schema version.',
    error: `The targeted version of ${passedSchemaVersion} is not supported. Version ${$joyfillSchemaVersion} must be used.`,
    details: {
      schemaVersion: $joyfillSchemaVersion,
      sdkVersion,
    }
  };

  // Validate schemaVersion
  const isVersionCompatible = compareVersions($joyfillSchemaVersion, passedSchemaVersion);

  if (!isVersionCompatible) {
    return schemaVersionErrorObject;
  }

  const valid = validate(doc);


  if (!valid) {
    return {
      ...schemaErrorObject,
      errors: validate.errors
    };
  }


  const duplicateFPWarnings = findDuplicateFieldPositionIds(doc);
  if (duplicateFPWarnings.length > 0) {
    console.warn('[JoyDoc Schema Warning] Duplicate fieldPosition _id(s) detected:', duplicateFPWarnings);
  }


  // If validation passes, return undefined
  return undefined;
};

export default validateSchema;

/**
 * Compares the current version of the JoyDoc schema with the passed version.
 * Returns true if they are compatible, false otherwise.
 *
 * @param {string} currentVersion - The current version of the JoyDoc schema.
 * @param {string} passedVersion - The version to compare against.
 * @returns {boolean} - True if versions are compatible, false otherwise.
 */

const compareVersions = (currentVersion, passedVersion) => currentVersion.split('.')[0] === passedVersion.split('.')[0];


/**
 * Checks all fieldPositions arrays in the document for duplicate _id values.
 * The compact() algorithm in @joyfill/layouts requires unique _id values;
 * duplicates cause array holes that crash the PDF renderer.
 *
 * @param {Object} doc - JoyDoc document
 * @returns {Object[]} Array of { pageId, pageIndex, fieldId, fieldPositionId, message }
 */
const findDuplicateFieldPositionIds = (doc) => {
  const warnings = [];
  if (!doc?.files || !Array.isArray(doc.files)) return warnings;

  doc.files.forEach((file) => {
    const sections = [];

    if (file.pages) {
      file.pages.forEach((page, pageIndex) => sections.push({ section: page, pageIndex }));
    }
    if (file.header) sections.push({ section: file.header, pageIndex: -1 });
    if (file.footer) sections.push({ section: file.footer, pageIndex: -1 });
    if (file.views && Array.isArray(file.views)) {
      file.views.forEach((view) => {
        if (view.pages) {
          view.pages.forEach((page, pageIndex) => sections.push({ section: page, pageIndex }));
        }
      });
    }

    sections.forEach(({ section, pageIndex }) => {
      if (!section.fieldPositions || !Array.isArray(section.fieldPositions)) return;

      const idCounts = {};
      section.fieldPositions.forEach((fp) => {
        if (fp._id) idCounts[fp._id] = (idCounts[fp._id] || 0) + 1;
      });

      Object.entries(idCounts).forEach(([id, count]) => {
        if (count > 1) {
          const matchingFps = section.fieldPositions.filter((fp) => fp._id === id);
          matchingFps.forEach((fp) => {
            warnings.push({
              pageId: section._id || 'unknown',
              pageIndex,
              fieldId: fp.field || 'unknown',
              fieldPositionId: fp._id,
              message: `Duplicate fieldPosition _id "${fp._id}" found ${count} times on this page. Each fieldPosition must have a unique _id. Duplicates cause layout errors in the PDF renderer.`,
            });
          });
        }
      });
    });
  });

  return warnings;
};